import React, { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { ReferralCode } from '@/components/common/ReferralCode';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserPlus, Link as LinkIcon, Gift } from 'lucide-react';
import API from '@/api/client';
import { toast } from 'sonner';

const CreateReferral = ({ userRole = 'customer' }) => {
  const [creating, setCreating] = useState(false);
  const [referralType, setReferralType] = useState('');
  const [newReferral, setNewReferral] = useState(null);

  const referralTypes = userRole === 'therapist'
    ? [
        { value: 'therapist_to_patient', label: 'Refer a Patient', payout: 'RM 35' },
        { value: 'therapist_to_therapist', label: 'Refer a Therapist', payout: 'RM 100' },
      ]
    : [
        { value: 'customer_to_patient', label: 'Refer a Friend', payout: 'RM 20' },
        { value: 'customer_to_corporate', label: 'Refer Your Company', payout: 'RM 50' },
      ];

  const handleCreate = async () => {
    if (!referralType) {
      toast.error('Please select a referral type');
      return;
    }

    try {
      setCreating(true);
      const endpoint = userRole === 'therapist' ? '/therapist/referrals' : '/customer/referrals';
      const response = await API.post(endpoint, { referral_type: referralType });
      setNewReferral(response.data);
      toast.success('Referral code generated!');
    } catch (error) {
      console.error('Error creating referral:', error);
      toast.error('Failed to create referral');
    } finally {
      setCreating(false);
    }
  };

  const shareUrl = newReferral
    ? `${window.location.origin}/signup?ref=${newReferral.referral_code}`
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile Logo"
              className="h-10"
            />
            <h1 className="font-heading text-xl font-bold text-gray-800">Create Referral</h1>
          </div>
          <Button
            onClick={() =>
              (window.location.href =
                userRole === 'therapist' ? '/therapist/dashboard' : '/customer/dashboard')
            }
            variant="outline"
          >
            Back
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
        <PageHeader
          title="Earn Rewards by Referring"
          subtitle="Share Physiomobile and get rewarded when they book sessions"
        />

        {!newReferral ? (
          <Card className="p-8">
            <div className="space-y-6">
              <div>
                <Label className="font-subheading text-sm font-medium mb-2 block">
                  Select Referral Type
                </Label>
                <Select value={referralType} onValueChange={setReferralType}>
                  <SelectTrigger data-testid="referral-type-select">
                    <SelectValue placeholder="Choose who you're referring..." />
                  </SelectTrigger>
                  <SelectContent>
                    {referralTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} - Earn {type.payout}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {referralTypes.map((type) => (
                  <div
                    key={type.value}
                    className="p-4 rounded-lg border-2 border-gray-200 hover:border-primary transition-colors cursor-pointer"
                    onClick={() => setReferralType(type.value)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <UserPlus className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold">{type.label}</h3>
                    </div>
                    <p className="text-2xl font-bold text-primary mb-1">{type.payout}</p>
                    <p className="text-xs text-muted-foreground">
                      {type.value.includes('patient')
                        ? 'After 2 completed sessions'
                        : type.value.includes('therapist')
                        ? 'After 10 completed sessions'
                        : 'After pilot scheduled'}
                    </p>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleCreate}
                disabled={!referralType || creating}
                className="w-full"
                size="lg"
                data-testid="create-referral-button"
              >
                {creating ? 'Generating...' : 'Generate Referral Code'}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="p-8 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-500 mx-auto flex items-center justify-center mb-4">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-gray-800 mb-2">
                  Referral Code Generated!
                </h2>
                <p className="text-muted-foreground">
                  Share this code and start earning rewards
                </p>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium mb-2 block">Your Referral Code</Label>
                <ReferralCode code={newReferral.referral_code} />
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium mb-2 block">Share Link</Label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      toast.success('Link copied!');
                    }}
                  >
                    <LinkIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-green-200">
                <p className="text-sm font-medium text-gray-800 mb-2">
                  How to earn your reward:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Share your referral code with friends or colleagues</li>
                  <li>They use your code when signing up</li>
                  <li>
                    {newReferral.referral.referral_type.includes('patient')
                      ? 'After they complete 2 sessions, you get paid!'
                      : newReferral.referral.referral_type.includes('therapist')
                      ? 'After they complete 10 sessions, you get paid!'
                      : 'After pilot is scheduled, you get paid!'}
                  </li>
                </ul>
              </div>
            </Card>

            <div className="text-center">
              <Button onClick={() => setNewReferral(null)} variant="outline">
                Create Another Referral
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreateReferral;